import { Card, Collapsible, InlineStack, BlockStack, Text, Icon, Tabs, DataTable, Button, SkeletonBodyText } from '@shopify/polaris';
import React, { useCallback, useEffect, useState } from 'react';
import { ChevronDownMinor } from '@shopify/polaris-icons';
import Table from 'antd/es/table';
import { DI, DIProps } from '../../../../../Core';
import { amazon_regions } from '../../../Onboarding/components/onboardinghelper';
import { getSourceShopID, getTotalProductCount } from '../../Utils/Helper';
import { viewOrderColumns } from '../../Utils/Constants';
import { useTranslation } from 'react-i18next';
interface props extends DIProps {
    row: any
}

const OrderDetails = (props: props) => {
    const { row } = props;
    const { t } = useTranslation();
    const [open, setOpen] = useState(true);
    const [selected, setSelected] = useState(0);
    const [shopify_order, setShopifyOrder] = useState<any>({});
    const [amazonItems, setAmazonItems] = useState<any>([]);
    const [shopifyItems, setShopifyItems] = useState<any>([]);
    const [showSkeleton, setShowSkeleton] = useState<boolean>(true);

    const handleToggle = useCallback(() => setOpen((open) => !open), []);

    /**
     * Handles the change of tabs.
     * @param {number} selectedTabIndex - The index of the selected tab.
     */
    const handleTabChange = useCallback((selectedTabIndex: number) => {
        setSelected(selectedTabIndex)
        if (selectedTabIndex === 1 && Object.keys(shopify_order).length === 0) {
            if (row?.targets?.filter((target: any) => target?.shop_id === getSourceShopID(props))[0]?.status !== 'failed' && row?.targets?.filter((target: any) => target?.shop_id === getSourceShopID(props))[0]?.hasOwnProperty("order_id"))
                getShopifyOrderItems(row?.targets?.filter((target: any) => target?.shop_id === getSourceShopID(props))[0]?.order_id)
            else setShowSkeleton(false)
        }
    }, [shopify_order, row]);

    /**
     * Fetches Shopify order items based on the provided ID.
     * @param {any} id - The ID of the Shopify order.
     * @returns {void}
     */
    const getShopifyOrderItems = (id: any): void => {
        let obj: any = {
            'filter[user_id][1]': props.redux.user_id,
            'filter[object_type][1]': "target_order",
            'filter[marketplace][1]': props?.redux?.current?.source?.marketplace,
            'filter[marketplace_shop_id][1]': props?.redux?.current?.source?._id,
            "filter[marketplace_reference_id][1]": id
        }
        props.di.GET('connector/order/getAll', obj).then((response: any) => {
            setShowSkeleton(false);
            if (response?.success) setShopifyOrder({ ...response?.data?.rows?.[0] });
        })
    }

    // Renders the Amazon order items based on the row data.
    useEffect(() => {
        let temp: any = [];

        row?.items
            && row?.items?.map((item: any, idx: number) => {
                temp?.push({
                    key: idx,
                    title: <Text variant="bodySm" breakWord as="span">
                        {item?.title || t("N/A")}
                    </Text>,
                    product: <BlockStack gap="0">
                        {
                            item?.sku &&
                            <InlineStack gap="100">
                                <Text variant="bodySm" as="span">
                                    {t("SKU")}:
                                </Text>
                                <Text variant="bodySm" as="span">
                                    {item?.sku || t("N/A")}
                                </Text>
                            </InlineStack>
                        }
                        {
                            item?.product_identifier &&
                            <InlineStack gap="100">
                                <Text variant="bodySm" as="span">
                                    {t("ASIN")}:
                                </Text>
                                <Button size='micro' variant="plain">{item?.product_identifier || t("N/A")}</Button>
                            </InlineStack>
                        }
                    </BlockStack>,
                    quantity: <Text variant="bodySm" as="span">{item?.qty || 0}</Text>,
                    price: <Text variant="bodySm" as="span">{amazon_regions.find(a => a?.currency === (row?.marketplace_currency ?? ''))?.['currency_symbol'] || ''} {
                        (row?.taxes_included ? (item?.marketplace_price - item?.tax?.marketplace_price) : item?.marketplace_price) || 0.00}</Text>,
                    vat: <Text variant="bodySm" as="span">{amazon_regions.find(a => a?.currency === (row?.marketplace_currency ?? ''))?.['currency_symbol'] || ''} {(row?.taxes_included ? item?.marketplace_price : (item?.marketplace_price + item?.tax?.marketplace_price)) || 0.00}</Text>
                })
            })

        setAmazonItems([...temp])
    }, [row])

    /**
     * Renders taxes for the order.
     * @returns {Array<Array<string>>} An array containing tax information.
     */
    const renderTaxes = (): Array<Array<string>> => {
        let arr: Array<any> = [];
        row?.taxes?.map((tax: any, idx: any) => {
            if (idx == 0) {
                arr?.push(["Tax", `${tax?.code} ${tax?.rate}% ${row?.taxes_included ? `(Included)` : ""}`, `${amazon_regions.find(a => a?.currency === (row?.marketplace_currency ?? ''))?.['currency_symbol'] || ''} ${tax?.marketplace_price || 0.00}`]);
            } else {
                arr?.push(["", `${tax?.code} ${tax?.rate}% ${row?.taxes_included ? `(Included)` : ""}`, `${amazon_regions.find(a => a?.currency === (row?.marketplace_currency ?? ''))?.['currency_symbol'] || ''} ${(tax?.marketplace_price || 0.00)}`]);
            }
        });

        return arr;
    };


    const rows = [
        [t('Shipping'), `${row?.shipping_details?.method ?? ""} (${amazon_regions.find(a => a?.currency === (row?.marketplace_currency ?? ''))?.['currency_symbol'] || ''}${row?.shipping_charge?.marketplace_price || 0.00})`, `${amazon_regions.find(a => a?.currency === (row?.marketplace_currency ?? ''))?.['currency_symbol'] || ''} ${row?.shipping_charge?.marketplace_price || 0.00}`],
        ...renderTaxes(),
        [
            t('Total'),
            `${amazon_regions.find(a => a?.currency === (row?.marketplace_currency ?? ''))?.['currency_symbol'] || ''} ${row?.total?.marketplace_price || 0.00}`,
        ],
    ];

    // Renders the Amazon data table for the order.
    const amazonDataTable: React.ReactNode = <Card padding='0' roundedAbove='sm'>
        <div className='Custom__Data--Table'>
            <DataTable
                showTotalsInFooter
                columnContentTypes={[
                    'text',
                    'numeric',
                    'numeric',
                ]}
                headings={[
                    t('Subtotal'),
                    getTotalProductCount(row),
                    `${amazon_regions.find(a => a?.currency === (row?.marketplace_currency ?? ''))?.['currency_symbol'] || ''} ${row?.sub_total?.marketplace_price || 0}`,
                ]}
                rows={rows}
                totals={['', '', `${amazon_regions.find(a => a?.currency === (row?.marketplace_currency ?? ''))?.['currency_symbol'] || ''} ${row?.total?.marketplace_price || 0}`]}
                totalsName={{
                    singular: t('Total (paid by customer)'),
                    plural: t('Total (paid by customer)'),
                }}
            />
        </div>
    </Card>

    // Renders Shopify items for the order.
    useEffect(() => {
        let temp: any = [];
        Object.keys(shopify_order).length !== 0 &&
            shopify_order?.items
            && shopify_order?.items?.map((item: any, idx: number) => {
                temp?.push({
                    key: idx,
                    title: <Text variant="bodySm" breakWord as="span">
                        {item?.title || t("N/A")}
                    </Text>,
                    product: <BlockStack gap="0">
                        {
                            item?.sku &&
                            <InlineStack gap="100">
                                <Text variant="bodySm" as="span">
                                    {t("SKU")}:
                                </Text>
                                <Text variant="bodySm" as="span">
                                    {item?.sku || t("N/A")}
                                </Text>
                            </InlineStack>
                        }
                        {
                            item?.product_identifier &&
                            <InlineStack gap="100">
                                <Text variant="bodySm" as="span">
                                    {t("ASIN")}:
                                </Text>
                                <Button size='micro' variant="plain">{item?.product_identifier || t("N/A")}</Button>
                            </InlineStack>
                        }
                    </BlockStack>,
                    quantity: <Text variant="bodySm" as="span">{item?.qty || 0}</Text>,
                    price: <Text variant="bodySm" as="span">{amazon_regions.find(a => a?.currency === (shopify_order?.marketplace_currency ?? ''))?.['currency_symbol'] || ''} {(row?.taxes_included ? (item?.marketplace_price - item?.tax?.marketplace_price) : item?.marketplace_price) || 0.00}</Text>,
                    vat: <Text variant="bodySm" as="span">{amazon_regions.find(a => a?.currency === (shopify_order?.marketplace_currency ?? ''))?.['currency_symbol'] || ''} {(shopify_order?.taxes_included ? item?.marketplace_price : (+item?.marketplace_price + +item?.tax?.marketplace_price)) || 0.00}</Text>
                })
            })

        setShopifyItems([...temp])
    }, [shopify_order])

    const tabs = [
        {
            id: 'amazon ',
            content: t('Amazon'),
            subcontent: <BlockStack gap="500">
                <Card padding="0" roundedAbove='sm'>
                    {
                        amazonItems?.length === 0 ?
                            <div className='Custom__Card--Sales'>
                                <Card>
                                    <Text as='p' tone='subdued'>{t("Your Amazon order items will show here.")}</Text>
                                </Card>
                            </div>
                            : <Table columns={viewOrderColumns(t)} dataSource={amazonItems} pagination={false} size='small' scroll={{ x: 700 }} />
                    }
                </Card>
                <Card padding="0" roundedAbove='sm'>{amazonDataTable}</Card>
            </BlockStack>,
        },
        {
            id: 'shopify',
            content: t('Shopify'),
            subcontent: <BlockStack gap="400">
                <Card padding="0" roundedAbove='sm'>
                    {
                        showSkeleton ?
                            <SkeletonBodyText />
                            :
                            shopifyItems?.length === 0 ?
                                <div className='Custom__Card--Sales'>
                                    <Card>
                                        <Text as='p' tone='subdued'>{t("Your order is not yet created on Shopify.")}</Text>
                                    </Card>
                                </div>
                                :
                                <Table columns={viewOrderColumns(t)} dataSource={shopifyItems} pagination={false} size='small' scroll={{ x: 700 }} />
                    }
                </Card>
            </BlockStack>,
        },
    ];

    return (
        <Card roundedAbove='sm'>
            <BlockStack gap="500">
                <div onClick={handleToggle} style={{ cursor: 'pointer' }}>
                    <InlineStack align="space-between" blockAlign='center'>
                        <Text variant="headingMd" as="h6">
                            {t("Order Details")} ({row?.items?.length || 0})
                        </Text>
                        <div>
                            <Icon
                                source={ChevronDownMinor}
                                tone="base"
                            />
                        </div>
                    </InlineStack>
                </div>
                <Collapsible open={open} id="basic-collapsible" transition={{ duration: '200ms', timingFunction: 'ease-in-out' }} expandOnPrint>
                    <BlockStack gap="400">
                        <Tabs tabs={tabs} selected={selected} onSelect={handleTabChange} />
                        {tabs[selected].subcontent}
                    </BlockStack>
                </Collapsible>
            </BlockStack>
        </Card>
    );
}

export default DI(OrderDetails);